import React, { useCallback, useEffect, useMemo, useState, Component } from 'react';
import { ReactFlow, Background, Controls, MiniMap, useNodesState, useEdgesState, Handle, Position, MarkerType, Panel } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { motion } from 'framer-motion';
import { MaximizeIcon } from 'lucide-react';
// --- Types & Constants ---
type Category = 'AI Core' | 'AI Agent' | 'Platform' | 'Infrastructure' | 'API' | 'Integration' | 'Service' | 'Data' | 'External';
const CATEGORY_COLORS: Record<Category, string> = {
  'AI Core': '#F59E0B',
  'AI Agent': '#852FF4',
  Platform: '#3B82F6',
  Infrastructure: '#10B981',
  API: '#EC4899',
  Integration: '#06B6D4',
  Service: '#9CA3AF',
  Data: '#F97316',
  External: '#64748B' // Slate
};
// --- Custom Node Component ---
const CustomNode = ({
  data
}: {
  data: {
    label: string;
    category: Category;
  };
}) => {
  const color = CATEGORY_COLORS[data.category];
  const isCore = data.category === 'AI Core';
  return <div className={`relative rounded-xl border bg-[#0E1C43]/90 backdrop-blur-sm shadow-xl min-w-[160px] ${isCore ? 'border-amber-500/50 shadow-[0_0_30px_rgba(245,158,11,0.2)]' : 'border-white/10'}`} data-id="element-357">
      <Handle type="target" position={Position.Top} className="opacity-0" data-id="element-358" />
      <Handle type="target" position={Position.Left} className="opacity-0" data-id="element-359" />

      <div className="px-4 py-3" data-id="element-360">
        <div className="flex items-center gap-2 mb-1.5" data-id="element-361">
          <div className="w-2 h-2 rounded-full" style={{
          backgroundColor: color,
          boxShadow: `0 0 8px ${color}`
        }} data-id="element-362" />
          <span className="text-[10px] font-bold tracking-wider uppercase" style={{
          color
        }} data-id="element-363">
            {data.category}
          </span>
        </div>
        <div className={`font-poppins font-semibold ${isCore ? 'text-xl text-white' : 'text-sm text-white/90'}`} data-id="element-364">
          {data.label}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className="opacity-0" data-id="element-365" />
      <Handle type="source" position={Position.Right} className="opacity-0" data-id="element-366" />
    </div>;
};
const nodeTypes = {
  custom: CustomNode
};
// --- Data: Infrastructure View ---
const infraNodes = [{
  id: 'core',
  type: 'custom',
  position: {
    x: 500,
    y: 400
  },
  data: {
    label: 'MISTR',
    category: 'AI Core'
  }
},
// Agents
{
  id: 'ag1',
  type: 'custom',
  position: {
    x: 500,
    y: 200
  },
  data: {
    label: 'Strategy Engine',
    category: 'AI Agent'
  }
}, {
  id: 'ag2',
  type: 'custom',
  position: {
    x: 750,
    y: 300
  },
  data: {
    label: 'Task Orchestrator',
    category: 'AI Agent'
  }
}, {
  id: 'ag3',
  type: 'custom',
  position: {
    x: 750,
    y: 500
  },
  data: {
    label: 'Chat Concierge',
    category: 'AI Agent'
  }
}, {
  id: 'ag4',
  type: 'custom',
  position: {
    x: 250,
    y: 500
  },
  data: {
    label: 'Content Writer',
    category: 'AI Agent'
  }
},
// Services
{
  id: 'svc1',
  type: 'custom',
  position: {
    x: 350,
    y: 150
  },
  data: {
    label: 'Language Router',
    category: 'Service'
  }
}, {
  id: 'svc2',
  type: 'custom',
  position: {
    x: 350,
    y: 300
  },
  data: {
    label: 'Voice Synthesizer',
    category: 'Service'
  }
}, {
  id: 'svc3',
  type: 'custom',
  position: {
    x: 350,
    y: 550
  },
  data: {
    label: 'Memory Sync',
    category: 'Service'
  }
}, {
  id: 'svc4',
  type: 'custom',
  position: {
    x: 500,
    y: 600
  },
  data: {
    label: 'Core Backend',
    category: 'Service'
  }
}, {
  id: 'svc5',
  type: 'custom',
  position: {
    x: 700,
    y: 650
  },
  data: {
    label: 'Tool Registry',
    category: 'Service'
  }
},
// APIs
{
  id: 'api1',
  type: 'custom',
  position: {
    x: 350,
    y: 0
  },
  data: {
    label: 'AI Language API',
    category: 'API'
  }
}, {
  id: 'api2',
  type: 'custom',
  position: {
    x: 150,
    y: 300
  },
  data: {
    label: 'Speech API',
    category: 'API'
  }
}, {
  id: 'api3',
  type: 'custom',
  position: {
    x: 250,
    y: 700
  },
  data: {
    label: 'Neural API',
    category: 'API'
  }
}, {
  id: 'api4',
  type: 'custom',
  position: {
    x: 850,
    y: 750
  },
  data: {
    label: 'Messaging API',
    category: 'API'
  }
},
// Platforms
{
  id: 'plat1',
  type: 'custom',
  position: {
    x: 150,
    y: 150
  },
  data: {
    label: 'Site Builder',
    category: 'Platform'
  }
}, {
  id: 'plat2',
  type: 'custom',
  position: {
    x: 950,
    y: 200
  },
  data: {
    label: 'Analytics Hub',
    category: 'Platform'
  }
}, {
  id: 'plat3',
  type: 'custom',
  position: {
    x: 1000,
    y: 350
  },
  data: {
    label: 'Project Tracker',
    category: 'Platform'
  }
}, {
  id: 'plat4',
  type: 'custom',
  position: {
    x: 1050,
    y: 500
  },
  data: {
    label: 'Workflow Engine',
    category: 'Platform'
  }
}, {
  id: 'plat5',
  type: 'custom',
  position: {
    x: 950,
    y: 600
  },
  data: {
    label: 'Team Chat',
    category: 'Platform'
  }
},
// Infrastructure
{
  id: 'inf1',
  type: 'custom',
  position: {
    x: 50,
    y: 200
  },
  data: {
    label: 'CDN Layer',
    category: 'Infrastructure'
  }
}, {
  id: 'inf2',
  type: 'custom',
  position: {
    x: 100,
    y: 400
  },
  data: {
    label: 'Cloud Compute',
    category: 'Infrastructure'
  }
}, {
  id: 'inf3',
  type: 'custom',
  position: {
    x: 50,
    y: 500
  },
  data: {
    label: 'Code Repository',
    category: 'Infrastructure'
  }
},
// Data
{
  id: 'dat1',
  type: 'custom',
  position: {
    x: 150,
    y: 600
  },
  data: {
    label: 'Knowledge Base',
    category: 'Data'
  }
}, {
  id: 'dat2',
  type: 'custom',
  position: {
    x: 150,
    y: 800
  },
  data: {
    label: 'Cloud Storage',
    category: 'Data'
  }
},
// Integrations
{
  id: 'int1',
  type: 'custom',
  position: {
    x: 450,
    y: 800
  },
  data: {
    label: 'CRM Connector',
    category: 'Integration'
  }
}, {
  id: 'int2',
  type: 'custom',
  position: {
    x: 650,
    y: 800
  },
  data: {
    label: 'Email Connector',
    category: 'Integration'
  }
}, {
  id: 'int3',
  type: 'custom',
  position: {
    x: 850,
    y: 850
  },
  data: {
    label: 'Calendar Connector',
    category: 'Integration'
  }
},
// External
{
  id: 'ext1',
  type: 'custom',
  position: {
    x: 0,
    y: 700
  },
  data: {
    label: 'Password Vault',
    category: 'External'
  }
}];
const createEdge = (source: string, target: string, label: string, animated = false) => ({
  id: `e-${source}-${target}`,
  source,
  target,
  label,
  animated,
  style: {
    stroke: '#475569',
    strokeWidth: 1.5
  },
  labelStyle: {
    fill: '#94A3B8',
    fontSize: 10,
    fontWeight: 500
  },
  labelBgStyle: {
    fill: '#0E1C43',
    fillOpacity: 0.8
  },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#475569',
    width: 15,
    height: 15
  }
});
const infraEdges = [createEdge('core', 'ag1', 'routes through', true), createEdge('core', 'ag2', 'manages', true), createEdge('core', 'ag3', 'speaks via', true), createEdge('core', 'ag4', 'directs', true), createEdge('core', 'svc4', 'uses', true), createEdge('core', 'svc3', 'pulls from', true), createEdge('svc1', 'api1', 'calls'), createEdge('svc2', 'api2', 'calls'), createEdge('svc3', 'dat1', 'indexes to'), createEdge('dat1', 'dat2', 'backed by'), createEdge('svc4', 'svc5', 'connects'), createEdge('svc5', 'int1', 'handles'), createEdge('svc5', 'int2', 'handles'), createEdge('svc5', 'int3', 'handles'), createEdge('ag1', 'svc1', 'uses'), createEdge('ag3', 'svc2', 'voice via'), createEdge('ag2', 'plat3', 'sends updates'), createEdge('ag2', 'plat4', 'automates'), createEdge('ag3', 'plat5', 'replies in'), createEdge('api4', 'plat5', 'connects'), createEdge('int1', 'api3', 'feeds'), createEdge('inf2', 'svc2', 'hosts'), createEdge('inf1', 'plat1', 'proxies to'), createEdge('inf3', 'inf2', 'deploys to'), createEdge('ext1', 'dat1', 'secures')];
// --- Data: Agent Team View ---
const teamNodes = [{
  id: 'core',
  type: 'custom',
  position: {
    x: 500,
    y: 400
  },
  data: {
    label: 'MISTR',
    category: 'AI Core'
  }
}, {
  id: 't1',
  type: 'custom',
  position: {
    x: 500,
    y: 150
  },
  data: {
    label: 'Operations Agent',
    category: 'AI Agent'
  }
}, {
  id: 't2',
  type: 'custom',
  position: {
    x: 750,
    y: 250
  },
  data: {
    label: 'Sales Agent',
    category: 'AI Agent'
  }
}, {
  id: 't3',
  type: 'custom',
  position: {
    x: 800,
    y: 500
  },
  data: {
    label: 'Marketing Agent',
    category: 'AI Agent'
  }
}, {
  id: 't4',
  type: 'custom',
  position: {
    x: 650,
    y: 700
  },
  data: {
    label: 'Finance Agent',
    category: 'AI Agent'
  }
}, {
  id: 't5',
  type: 'custom',
  position: {
    x: 350,
    y: 700
  },
  data: {
    label: 'HR Agent',
    category: 'AI Agent'
  }
}, {
  id: 't6',
  type: 'custom',
  position: {
    x: 200,
    y: 500
  },
  data: {
    label: 'Support Agent',
    category: 'AI Agent'
  }
}, {
  id: 't7',
  type: 'custom',
  position: {
    x: 250,
    y: 250
  },
  data: {
    label: 'Research Agent',
    category: 'AI Agent'
  }
},
// Tools they use
{
  id: 'tool1',
  type: 'custom',
  position: {
    x: 500,
    y: 0
  },
  data: {
    label: 'Project Tracker',
    category: 'Platform'
  }
}, {
  id: 'tool2',
  type: 'custom',
  position: {
    x: 950,
    y: 150
  },
  data: {
    label: 'CRM System',
    category: 'Platform'
  }
}, {
  id: 'tool3',
  type: 'custom',
  position: {
    x: 1000,
    y: 600
  },
  data: {
    label: 'Social Scheduler',
    category: 'Platform'
  }
}, {
  id: 'tool4',
  type: 'custom',
  position: {
    x: 700,
    y: 850
  },
  data: {
    label: 'Accounting Hub',
    category: 'Platform'
  }
}, {
  id: 'tool5',
  type: 'custom',
  position: {
    x: 300,
    y: 850
  },
  data: {
    label: 'Payroll System',
    category: 'Platform'
  }
}, {
  id: 'tool6',
  type: 'custom',
  position: {
    x: 0,
    y: 600
  },
  data: {
    label: 'Helpdesk',
    category: 'Platform'
  }
}, {
  id: 'tool7',
  type: 'custom',
  position: {
    x: 50,
    y: 150
  },
  data: {
    label: 'Data Scraper',
    category: 'Platform'
  }
}];
const teamEdges = [createEdge('core', 't1', 'delegates', true), createEdge('core', 't2', 'delegates', true), createEdge('core', 't3', 'delegates', true), createEdge('core', 't4', 'delegates', true), createEdge('core', 't5', 'delegates', true), createEdge('core', 't6', 'delegates', true), createEdge('core', 't7', 'delegates', true), createEdge('t1', 'tool1', 'updates'), createEdge('t2', 'tool2', 'logs calls'), createEdge('t3', 'tool3', 'posts'), createEdge('t4', 'tool4', 'reconciles'), createEdge('t5', 'tool5', 'manages'), createEdge('t6', 'tool6', 'resolves'), createEdge('t7', 'tool7', 'queries'), createEdge('t2', 't1', 'notifies'), createEdge('t6', 't2', 'escalates')];
// --- Data: KB Data Flow View ---
const kbNodes = [{
  id: 'core',
  type: 'custom',
  position: {
    x: 500,
    y: 400
  },
  data: {
    label: 'MISTR',
    category: 'AI Core'
  }
},
// Data Sources (top)
{
  id: 'kb1',
  type: 'custom',
  position: {
    x: 200,
    y: 50
  },
  data: {
    label: 'Document Upload',
    category: 'Data'
  }
}, {
  id: 'kb2',
  type: 'custom',
  position: {
    x: 500,
    y: 50
  },
  data: {
    label: 'Website Crawl',
    category: 'Data'
  }
}, {
  id: 'kb3',
  type: 'custom',
  position: {
    x: 800,
    y: 50
  },
  data: {
    label: 'CRM Import',
    category: 'Integration'
  }
},
// Processing Layer
{
  id: 'kb4',
  type: 'custom',
  position: {
    x: 200,
    y: 200
  },
  data: {
    label: 'Text Extractor',
    category: 'Service'
  }
}, {
  id: 'kb5',
  type: 'custom',
  position: {
    x: 500,
    y: 200
  },
  data: {
    label: 'Content Parser',
    category: 'Service'
  }
}, {
  id: 'kb6',
  type: 'custom',
  position: {
    x: 800,
    y: 200
  },
  data: {
    label: 'Data Normalizer',
    category: 'Service'
  }
},
// Embedding & Indexing
{
  id: 'kb7',
  type: 'custom',
  position: {
    x: 350,
    y: 350
  },
  data: {
    label: 'Embedding Engine',
    category: 'AI Agent'
  }
}, {
  id: 'kb8',
  type: 'custom',
  position: {
    x: 650,
    y: 350
  },
  data: {
    label: 'Vector Indexer',
    category: 'AI Agent'
  }
},
// Storage
{
  id: 'kb9',
  type: 'custom',
  position: {
    x: 250,
    y: 550
  },
  data: {
    label: 'Vector Database',
    category: 'Infrastructure'
  }
}, {
  id: 'kb10',
  type: 'custom',
  position: {
    x: 500,
    y: 600
  },
  data: {
    label: 'Knowledge Graph',
    category: 'Data'
  }
}, {
  id: 'kb11',
  type: 'custom',
  position: {
    x: 750,
    y: 550
  },
  data: {
    label: 'Document Store',
    category: 'Infrastructure'
  }
},
// Retrieval & Output
{
  id: 'kb12',
  type: 'custom',
  position: {
    x: 300,
    y: 750
  },
  data: {
    label: 'Semantic Search',
    category: 'AI Agent'
  }
}, {
  id: 'kb13',
  type: 'custom',
  position: {
    x: 550,
    y: 750
  },
  data: {
    label: 'Context Builder',
    category: 'AI Agent'
  }
}, {
  id: 'kb14',
  type: 'custom',
  position: {
    x: 800,
    y: 750
  },
  data: {
    label: 'Response Generator',
    category: 'AI Agent'
  }
},
// APIs
{
  id: 'kb15',
  type: 'custom',
  position: {
    x: 50,
    y: 200
  },
  data: {
    label: 'Embedding API',
    category: 'API'
  }
}, {
  id: 'kb16',
  type: 'custom',
  position: {
    x: 1000,
    y: 200
  },
  data: {
    label: 'Search API',
    category: 'API'
  }
},
// External
{
  id: 'kb17',
  type: 'custom',
  position: {
    x: 1000,
    y: 500
  },
  data: {
    label: 'Client Portal',
    category: 'Platform'
  }
}, {
  id: 'kb18',
  type: 'custom',
  position: {
    x: 50,
    y: 500
  },
  data: {
    label: 'Backup Storage',
    category: 'External'
  }
}];
const kbEdges = [
// Sources → Processing
createEdge('kb1', 'kb4', 'extracts', true), createEdge('kb2', 'kb5', 'parses', true), createEdge('kb3', 'kb6', 'normalizes', true),
// Processing → Embedding
createEdge('kb4', 'kb7', 'feeds'), createEdge('kb5', 'kb7', 'feeds'), createEdge('kb6', 'kb8', 'feeds'),
// Embedding → Core
createEdge('kb7', 'core', 'enriches', true), createEdge('kb8', 'core', 'indexes to', true),
// Core → Storage
createEdge('core', 'kb9', 'stores vectors', true), createEdge('core', 'kb10', 'builds graph', true), createEdge('core', 'kb11', 'archives', true),
// Storage → Retrieval
createEdge('kb9', 'kb12', 'queries'), createEdge('kb10', 'kb13', 'provides context'), createEdge('kb11', 'kb13', 'retrieves docs'),
// Retrieval → Output
createEdge('kb12', 'kb13', 'ranks results'), createEdge('kb13', 'kb14', 'assembles'),
// APIs
createEdge('kb7', 'kb15', 'calls'), createEdge('kb16', 'kb12', 'powers'),
// External connections
createEdge('kb14', 'kb17', 'serves'), createEdge('kb9', 'kb18', 'backs up')];
// --- Main Component ---
export function MindMapDemo() {
  const [activeTab, setActiveTab] = useState<'infra' | 'team' | 'kb'>('infra');
  const getDataForTab = (tab: 'infra' | 'team' | 'kb') => {
    switch (tab) {
      case 'infra':
        return {
          nodes: infraNodes,
          edges: infraEdges
        };
      case 'team':
        return {
          nodes: teamNodes,
          edges: teamEdges
        };
      case 'kb':
        return {
          nodes: kbNodes,
          edges: kbEdges
        };
    }
  };
  const {
    nodes: initialNodes,
    edges: initialEdges
  } = getDataForTab(activeTab);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  // Update nodes/edges when tab changes
  useEffect(() => {
    const {
      nodes: newNodes,
      edges: newEdges
    } = getDataForTab(activeTab);
    setNodes(newNodes);
    setEdges(newEdges);
  }, [activeTab, setNodes, setEdges]);
  return <div className="w-full h-[850px] rounded-2xl border border-white/10 bg-[#0F1D3A] overflow-hidden relative shadow-2xl flex flex-col" data-id="element-367">
      {/* Header / Tabs */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-[#0F1D3A] to-transparent pointer-events-none" data-id="element-368">
        <div className="flex items-center gap-2 pointer-events-auto bg-[#0E1C43]/80 backdrop-blur-md p-1 rounded-lg border border-white/5" data-id="element-369">
          <button onClick={() => setActiveTab('infra')} className={`px-4 py-2 rounded-md text-sm font-poppins font-semibold transition-colors ${activeTab === 'infra' ? 'bg-purple text-white' : 'text-steel hover:text-white'}`} data-id="element-370">
            Infrastructure
          </button>
          <button onClick={() => setActiveTab('team')} className={`px-4 py-2 rounded-md text-sm font-poppins font-semibold transition-colors ${activeTab === 'team' ? 'bg-purple text-white' : 'text-steel hover:text-white'}`} data-id="element-371">
            Agent Team
          </button>
          <button onClick={() => setActiveTab('kb')} className={`px-4 py-2 rounded-md text-sm font-poppins font-semibold transition-colors ${activeTab === 'kb' ? 'bg-purple text-white' : 'text-steel hover:text-white'}`} data-id="element-372">
            KB Data Flow
          </button>
        </div>

        <div className="flex items-center gap-3 pointer-events-auto" data-id="element-373">
          <div className="text-xs text-steel font-medium bg-[#0E1C43]/80 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/5" data-id="element-374">
            {nodes.length} nodes · {edges.length} edges
          </div>
          <a href="https://ms-mindmap-5vgdkry6lq-uc.a.run.app/mindstream" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-purple/90 hover:bg-purple text-white text-xs font-semibold px-3 py-1.5 rounded-lg border border-purple/50 transition-all hover:shadow-[0_0_15px_rgba(124,58,237,0.4)]" data-id="element-375">
            <MaximizeIcon className="w-3.5 h-3.5" data-id="element-376" />
            Full Map
          </a>
        </div>
      </div>

      {/* React Flow Canvas */}
      <div className="flex-1 w-full h-full" data-id="element-377">
        <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} nodeTypes={nodeTypes} fitView fitViewOptions={{
        padding: 0.2
      }} minZoom={0.2} maxZoom={2} proOptions={{
        hideAttribution: true
      }} className="bg-[#0F1D3A]" data-id="element-378">
          <Background color="#ffffff" gap={24} size={1} opacity={0.03} data-id="element-379" />
          <Controls className="bg-[#0E1C43]/80 backdrop-blur-md border border-white/10 fill-white !text-white" showInteractive={false} data-id="element-380" />
          <MiniMap className="bg-[#0E1C43]/80 backdrop-blur-md border border-white/10 !rounded-lg overflow-hidden" nodeColor={n => CATEGORY_COLORS[n.data.category as Category] || '#fff'} maskColor="rgba(10, 10, 15, 0.7)" data-id="element-381" />

          {/* Legend Panel */}
          <Panel position="bottom-left" className="bg-[#0E1C43]/80 backdrop-blur-md border border-white/10 p-4 rounded-xl mb-4 ml-4" data-id="element-382">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2" data-id="element-383">
              {Object.entries(CATEGORY_COLORS).map(([category, color]) => <div key={category} className="flex items-center gap-2" data-id="element-384">
                  <div className="w-2.5 h-2.5 rounded-full" style={{
                backgroundColor: color
              }} data-id="element-385" />
                  <span className="text-xs text-steel font-medium" data-id="element-386">
                    {category}
                  </span>
                </div>)}
            </div>
          </Panel>
        </ReactFlow>
      </div>
    </div>;
}